import React, { useState } from 'react';
import styled from 'styled-components';
import { FiUpload, FiCheck, FiAlertCircle, FiPackage, FiMapPin, FiAward, FiDollarSign, FiLock, FiUnlock, FiTrendingUp, FiShield, FiFileText, FiCamera, FiCalendar, FiThermometer, FiDroplet, FiUser, FiTruck } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useKYC } from '../../contexts/KYCContext';
import { useNavigate } from 'react-router-dom';

const TokenizationContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a3e 0%, #2d1b69 100%);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.8rem;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #a0a0a0;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
`;

const TokenizationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const TokenizationPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
`;

const SectionTitle = styled.h2`
  color: #fff;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: #fff;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: #fff;
  font-size: 1rem;
  
  &:focus {
    border-color: #4facfe;
    box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
    outline: none;
  }
  
  &::placeholder {
    color: #a0a0a0;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: #fff;
  font-size: 1rem;
  
  &:focus {
    border-color: #4facfe;
    box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
    outline: none;
  }
  
  option {
    background: #1a1a3e;
    color: #fff;
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
  
  &:hover {
    border-color: #4facfe;
    background: rgba(79, 172, 254, 0.05);
  }
  
  .upload-icon {
    font-size: 3rem;
    color: #4facfe;
    margin-bottom: 1rem;
  }
  
  .upload-text {
    color: #fff;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
  
  .upload-hint {
    color: #a0a0a0;
    font-size: 0.9rem;
  }
`;

const UploadedFile = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 10px;
  margin-bottom: 1rem;
  
  .file-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #00ff88;
  }
`;

const RequirementCard = styled.div`
  background: ${props => 
    props.completed ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 193, 7, 0.1)'
  };
  border: 1px solid ${props => 
    props.completed ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 193, 7, 0.3)'
  };
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  
  .requirement-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
    
    .requirement-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: ${props => props.completed ? '#00ff88' : '#ffc107'};
      font-weight: 600;
    }
    
    .status-badge {
      padding: 0.3rem 0.8rem;
      background: ${props => props.completed ? '#00ff88' : '#ffc107'};
      color: #000;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 600;
    }
  }
  
  .requirement-description {
    color: #a0a0a0;
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const TokenizationSummary = styled.div`
  background: rgba(79, 172, 254, 0.1);
  border: 1px solid rgba(79, 172, 254, 0.3);
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;
  
  .summary-title {
    color: #4facfe;
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
  
  .summary-item {
    .label {
      color: #a0a0a0;
      font-size: 0.9rem;
      margin-bottom: 0.3rem;
    }
    
    .value {
      color: #fff;
      font-size: 1.1rem;
      font-weight: 600;
    }
  }
`;

const TokenizeButton = styled.button`
  width: 100%;
  padding: 1.2rem 2rem;
  background: ${props => props.disabled ? 
    'rgba(255, 255, 255, 0.1)' : 
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
  };
  color: ${props => props.disabled ? '#666' : '#000'};
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.disabled ? 
      'rgba(255, 255, 255, 0.1)' : 
      'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)'
    };
  }
  
  &:disabled {
    opacity: 0.5;
  }
`;

const ProcessStep = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 10px;
  margin-bottom: 1rem;
  
  .step-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${props => props.completed ? '#00ff88' : props.active ? '#4facfe' : 'rgba(255, 255, 255, 0.1)'};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.completed || props.active ? '#000' : '#666'};
  }
  
  .step-content {
    flex: 1;
    
    .step-title {
      color: #fff;
      font-weight: 600;
      margin-bottom: 0.3rem;
    }
    
    .step-description {
      color: #a0a0a0;
      font-size: 0.9rem;
    }
  }
`;

function SugarTokenization() {
  const { kycStatus, canAccessPhysicalDelivery } = useKYC();
  const navigate = useNavigate();
  
  const [tokenizationData, setTokenizationData] = useState({
    // Sugar Details
    sugarType: '',
    icumsaRating: '',
    quantity: '',
    harvestDate: '',
    origin: '',
    
    // Warehouse Information
    warehouseLocation: '',
    warehouseReceipt: null,
    storageConditions: '',
    
    // Quality Certifications
    qualityCertificate: null,
    organicCertificate: null,
    fairTradeCertificate: null,
    
    // Ownership Documentation
    ownershipProof: null,
    invoiceDocument: null,
    
    // Token Configuration
    tokenSymbol: '',
    totalSupply: '',
    decimals: '18',
    
    // Inspection
    inspectionReport: null,
    inspectionDate: '',
    inspectorName: ''
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setTokenizationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (field, file) => {
    setTokenizationData(prev => ({
      ...prev,
      [field]: file
    }));
    toast.success(`${file.name} uploaded successfully`);
  };

  const validateTokenization = () => {
    if (!canAccessPhysicalDelivery()) {
      toast.error('Enhanced KYC verification required for sugar tokenization');
      return false;
    }
    
    const requiredFields = [
      'sugarType', 'icumsaRating', 'quantity', 'harvestDate', 'origin',
      'warehouseLocation', 'tokenSymbol', 'totalSupply'
    ];
    
    const missingFields = requiredFields.filter(field => !tokenizationData[field]);
    
    if (missingFields.length > 0) {
      toast.error(`Please complete all required fields: ${missingFields.join(', ')}`);
      return false;
    }
    
    if (!tokenizationData.warehouseReceipt || !tokenizationData.qualityCertificate || !tokenizationData.ownershipProof) {
      toast.error('Please upload all required documents');
      return false;
    }
    
    return true;
  };

  const handleTokenization = async () => {
    if (!validateTokenization()) return;
    
    setLoading(true);
    
    try {
      // Simulate tokenization process
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      toast.success('Sugar tokenization initiated successfully!');
      
      setTimeout(() => {
        toast.success('Core contract deployed and tokens minted!');
      }, 1000);
      
      setTimeout(() => {
        toast.success(`${tokenizationData.totalSupply} ${tokenizationData.tokenSymbol} tokens now available for trading`);
      }, 2000);
      
      // Reset form
      setCurrentStep(5); // Success step
      
    } catch (error) {
      toast.error('Tokenization failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const requirements = [
    {
      id: 'kyc',
      title: 'Enhanced KYC Verification',
      description: 'Complete business verification with sugar handling documentation',
      completed: canAccessPhysicalDelivery(),
      icon: FiUser
    },
    {
      id: 'warehouse',
      title: 'Warehouse Receipt',
      description: 'Valid warehouse receipt from approved storage facility',
      completed: !!tokenizationData.warehouseReceipt,
      icon: FiMapPin
    },
    {
      id: 'quality',
      title: 'Quality Certificate',
      description: 'ICUMSA rating and quality analysis from certified laboratory',
      completed: !!tokenizationData.qualityCertificate,
      icon: FiAward
    },
    {
      id: 'ownership',
      title: 'Ownership Proof',
      description: 'Legal documentation proving ownership of physical sugar',
      completed: !!tokenizationData.ownershipProof,
      icon: FiShield
    }
  ];

  const processSteps = [
    {
      id: 1,
      title: 'Sugar Registration',
      description: 'Register physical sugar details and specifications',
      icon: FiPackage,
      completed: currentStep > 1,
      active: currentStep === 1
    },
    {
      id: 2,
      title: 'Document Verification',
      description: 'Upload and verify all required documentation',
      icon: FiFileText,
      completed: currentStep > 2,
      active: currentStep === 2
    },
    {
      id: 3,
      title: 'Quality Inspection',
      description: 'Third-party inspection and quality verification',
      icon: FiAward,
      completed: currentStep > 3,
      active: currentStep === 3
    },
    {
      id: 4,
      title: 'Token Configuration',
      description: 'Configure token parameters and blockchain deployment',
      icon: FiDollarSign,
      completed: currentStep > 4,
      active: currentStep === 4
    },
    {
      id: 5,
      title: 'Tokenization Complete',
      description: 'Tokens minted and available for trading',
      icon: FiTrendingUp,
      completed: currentStep === 5,
      active: currentStep === 5
    }
  ];

  if (!canAccessPhysicalDelivery()) {
    return (
      <TokenizationContainer>
        <Header>
          <Title>Sugar Tokenization</Title>
          <Subtitle>Convert your physical sugar holdings into tradeable digital tokens</Subtitle>
        </Header>
        
        <div style={{ 
          background: 'rgba(255, 107, 107, 0.1)', 
          border: '1px solid rgba(255, 107, 107, 0.3)',
          borderRadius: '15px',
          padding: '2rem',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <FiLock style={{ fontSize: '3rem', color: '#ff6b6b', marginBottom: '1rem' }} />
          <h2 style={{ color: '#ff6b6b', marginBottom: '1rem' }}>Enhanced KYC Required</h2>
          <p style={{ color: '#a0a0a0', marginBottom: '2rem' }}>
            Sugar tokenization requires enhanced KYC verification with business documentation and sugar handling credentials.
          </p>
          <button
            onClick={() => navigate('/kyc')}
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: '#000',
              border: 'none',
              borderRadius: '10px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Complete Enhanced KYC
          </button>
        </div>
      </TokenizationContainer>
    );
  }

  return (
    <TokenizationContainer>
      <Header>
        <Title>Sugar Tokenization Platform</Title>
        <Subtitle>
          Convert your physical sugar holdings into blockchain-backed digital tokens. 
          Maintain full custody while enabling global liquidity and transparent trading.
        </Subtitle>
      </Header>

      {/* Process Steps */}
      <div style={{ marginBottom: '2rem' }}>
        {processSteps.map(step => (
          <ProcessStep key={step.id} completed={step.completed} active={step.active}>
            <div className="step-icon">
              <step.icon />
            </div>
            <div className="step-content">
              <div className="step-title">{step.title}</div>
              <div className="step-description">{step.description}</div>
            </div>
          </ProcessStep>
        ))}
      </div>

      <TokenizationGrid>
        {/* Tokenization Form */}
        <TokenizationPanel>
          <SectionTitle>
            <FiPackage />
            Sugar Registration
          </SectionTitle>

          <FormGrid>
            <FormGroup>
              <Label>Sugar Type *</Label>
              <Select
                value={tokenizationData.sugarType}
                onChange={(e) => handleInputChange('sugarType', e.target.value)}
              >
                <option value="">Select sugar type</option>
                <option value="refined_white">Refined White Sugar</option>
                <option value="raw_sugar">Raw Sugar</option>
                <option value="brown_sugar">Brown Sugar</option>
                <option value="organic_sugar">Organic Sugar</option>
                <option value="fair_trade_sugar">Fair Trade Sugar</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>ICUMSA Rating *</Label>
              <Select
                value={tokenizationData.icumsaRating}
                onChange={(e) => handleInputChange('icumsaRating', e.target.value)}
              >
                <option value="">Select ICUMSA rating</option>
                <option value="icumsa_30">ICUMSA 30 (Premium White)</option>
                <option value="icumsa_45">ICUMSA 45 (White Sugar)</option>
                <option value="icumsa_100">ICUMSA 100 (Plantation White)</option>
                <option value="icumsa_150">ICUMSA 150 (Golden Sugar)</option>
                <option value="icumsa_600">ICUMSA 600 (Raw Sugar)</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Quantity (kg) *</Label>
              <Input
                type="number"
                value={tokenizationData.quantity}
                onChange={(e) => handleInputChange('quantity', e.target.value)}
                placeholder="Enter quantity in kilograms"
                min="1000"
              />
            </FormGroup>

            <FormGroup>
              <Label>Harvest Date *</Label>
              <Input
                type="date"
                value={tokenizationData.harvestDate}
                onChange={(e) => handleInputChange('harvestDate', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Origin Country *</Label>
              <Select
                value={tokenizationData.origin}
                onChange={(e) => handleInputChange('origin', e.target.value)}
              >
                <option value="">Select origin</option>
                <option value="brazil">Brazil</option>
                <option value="india">India</option>
                <option value="thailand">Thailand</option>
                <option value="australia">Australia</option>
                <option value="guatemala">Guatemala</option>
                <option value="mexico">Mexico</option>
                <option value="philippines">Philippines</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Warehouse Location *</Label>
              <Select
                value={tokenizationData.warehouseLocation}
                onChange={(e) => handleInputChange('warehouseLocation', e.target.value)}
              >
                <option value="">Select warehouse</option>
                <option value="dubai_dmcc">Dubai DMCC - Commodities Centre</option>
                <option value="singapore_port">Singapore Port Authority</option>
                <option value="swiss_vault">Swiss Commodity Vault</option>
                <option value="london_metals">London Metals Exchange Warehouse</option>
                <option value="chicago_board">Chicago Board of Trade Facility</option>
              </Select>
            </FormGroup>
          </FormGrid>

          {/* Document Uploads */}
          <SectionTitle style={{ marginTop: '2rem' }}>
            <FiFileText />
            Required Documentation
          </SectionTitle>

          <FormGroup>
            <Label>Warehouse Receipt *</Label>
            {tokenizationData.warehouseReceipt ? (
              <UploadedFile>
                <div className="file-info">
                  <FiCheck />
                  <span>{tokenizationData.warehouseReceipt.name}</span>
                </div>
              </UploadedFile>
            ) : (
              <FileUploadArea onClick={() => document.getElementById('warehouse-receipt').click()}>
                <FiUpload className="upload-icon" />
                <div className="upload-text">Upload Warehouse Receipt</div>
                <div className="upload-hint">Official receipt from approved warehouse facility</div>
                <input
                  id="warehouse-receipt"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files[0] && handleFileUpload('warehouseReceipt', e.target.files[0])}
                />
              </FileUploadArea>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Quality Certificate *</Label>
            {tokenizationData.qualityCertificate ? (
              <UploadedFile>
                <div className="file-info">
                  <FiCheck />
                  <span>{tokenizationData.qualityCertificate.name}</span>
                </div>
              </UploadedFile>
            ) : (
              <FileUploadArea onClick={() => document.getElementById('quality-cert').click()}>
                <FiUpload className="upload-icon" />
                <div className="upload-text">Upload Quality Certificate</div>
                <div className="upload-hint">Laboratory analysis with ICUMSA rating</div>
                <input
                  id="quality-cert"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files[0] && handleFileUpload('qualityCertificate', e.target.files[0])}
                />
              </FileUploadArea>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Ownership Proof *</Label>
            {tokenizationData.ownershipProof ? (
              <UploadedFile>
                <div className="file-info">
                  <FiCheck />
                  <span>{tokenizationData.ownershipProof.name}</span>
                </div>
              </UploadedFile>
            ) : (
              <FileUploadArea onClick={() => document.getElementById('ownership-proof').click()}>
                <FiUpload className="upload-icon" />
                <div className="upload-text">Upload Ownership Documentation</div>
                <div className="upload-hint">Purchase invoice, title deed, or ownership certificate</div>
                <input
                  id="ownership-proof"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files[0] && handleFileUpload('ownershipProof', e.target.files[0])}
                />
              </FileUploadArea>
            )}
          </FormGroup>

          {/* Token Configuration */}
          <SectionTitle style={{ marginTop: '2rem' }}>
            <FiDollarSign />
            Token Configuration
          </SectionTitle>

          <FormGrid>
            <FormGroup>
              <Label>Token Symbol *</Label>
              <Input
                type="text"
                value={tokenizationData.tokenSymbol}
                onChange={(e) => handleInputChange('tokenSymbol', e.target.value.toUpperCase())}
                placeholder="e.g., SUGAR-BR-001"
                maxLength="12"
              />
            </FormGroup>

            <FormGroup>
              <Label>Total Token Supply *</Label>
              <Input
                type="number"
                value={tokenizationData.totalSupply}
                onChange={(e) => handleInputChange('totalSupply', e.target.value)}
                placeholder="Number of tokens to mint"
              />
            </FormGroup>
          </FormGrid>

        </TokenizationPanel>

        {/* Requirements & Summary */}
        <div>
          {/* Requirements Checklist */}
          <TokenizationPanel style={{ marginBottom: '2rem' }}>
            <SectionTitle>
              <FiShield />
              Tokenization Requirements
            </SectionTitle>

            {requirements.map(requirement => {
              const Icon = requirement.icon;
              return (
                <RequirementCard key={requirement.id} completed={requirement.completed}>
                  <div className="requirement-header">
                    <div className="requirement-title">
                      <Icon />
                      {requirement.title}
                    </div>
                    <div className="status-badge">
                      {requirement.completed ? 'Complete' : 'Required'}
                    </div>
                  </div>
                  <div className="requirement-description">
                    {requirement.description}
                  </div>
                </RequirementCard>
              );
            })}
          </TokenizationPanel>

          {/* Tokenization Summary */}
          {tokenizationData.quantity && tokenizationData.sugarType && (
            <TokenizationSummary>
              <div className="summary-title">
                <FiTrendingUp />
                Tokenization Summary
              </div>
              
              <div className="summary-grid">
                <div className="summary-item">
                  <div className="label">Sugar Type</div>
                  <div className="value">{tokenizationData.sugarType.replace('_', ' ')}</div>
                </div>
                
                <div className="summary-item">
                  <div className="label">Quality Rating</div>
                  <div className="value">{tokenizationData.icumsaRating.replace('_', ' ')}</div>
                </div>
                
                <div className="summary-item">
                  <div className="label">Total Quantity</div>
                  <div className="value">{tokenizationData.quantity} kg</div>
                </div>
                
                <div className="summary-item">
                  <div className="label">Token Symbol</div>
                  <div className="value">{tokenizationData.tokenSymbol || 'TBD'}</div>
                </div>
                
                <div className="summary-item">
                  <div className="label">Token Supply</div>
                  <div className="value">{tokenizationData.totalSupply || 'TBD'}</div>
                </div>
                
                <div className="summary-item">
                  <div className="label">Estimated Value</div>
                  <div className="value">${(tokenizationData.quantity * 0.45).toLocaleString()}</div>
                </div>
              </div>

              <TokenizeButton
                disabled={!validateTokenization() || loading}
                onClick={handleTokenization}
              >
                {loading ? '🔄 Processing Tokenization...' : '🚀 Tokenize Sugar Holdings'}
              </TokenizeButton>
            </TokenizationSummary>
          )}
        </div>
      </TokenizationGrid>
    </TokenizationContainer>
  );
}

export default SugarTokenization; 